import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiShoppingCart, 
  FiHeart, 
  FiShare2, 
  FiInfo, 
  FiMinus, 
  FiPlus, 
  FiEye, 
  FiArrowRight, 
  FiAlertCircle, 
  FiX, 
  FiChevronLeft, 
  FiChevronRight, 
  FiCheck,
  FiStar,
  FiThumbsUp
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { userApi } from '../../api/userApi';

const ProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [isWishlistLoading, setIsWishlistLoading] = useState(true);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const imageRef = useRef(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showImageViewer, setShowImageViewer] = useState(false);

  useEffect(() => {
    const loadWishlist = () => {
      const savedWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      setWishlistItems(savedWishlist);
    };

    loadWishlist();
    window.addEventListener('wishlistUpdated', loadWishlist);
    
    return () => {
      window.removeEventListener('wishlistUpdated', loadWishlist);
    };
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is typical mobile breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (showFullScreen) {
      document.body.classList.add('fullscreen-active');
    } else {
      document.body.classList.remove('fullscreen-active');
    }
    return () => {
      document.body.classList.remove('fullscreen-active');
    };
  }, [showFullScreen]);

  const handleMouseMove = (e) => {
    const { left, top, width, height } = imageRef.current.getBoundingClientRect();
    // Calculate position relative to the image
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setMagnifierPosition({ x, y });
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.name,
          text: `Check out this amazing ${product.name} at Nilormy!`,
          url: window.location.href,
        });
      } else {
        // Fallback for browsers that don't support native sharing
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard');
      }
    } catch (err) {
      console.error('Error sharing:', err);
      toast.error('Failed to share');
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        
        // Split the API calls to handle errors separately
        let productData;
        let productReviews = [];
        
        try {
          productData = await userApi.getProductDetail(id);
        } catch (error) {
          console.error('Error fetching product details:', error);
          toast.error('Failed to load product details');
          navigate('/products');
          return;
        }
        
        try {
          // Try to get reviews, but don't fail if it doesn't work
          const allReviews = await userApi.getProductReviews();
          
          // Only process reviews if we got a valid response
          if (Array.isArray(allReviews)) {
            productReviews = allReviews
              .filter(review => review.product_details?.id === parseInt(id))
              .map(review => ({
                id: review.id,
                rating: review.rating,
                review: review.description,
                date: new Date(review.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }),
                customerName: review.customer_name,
                productDetails: review.product_details
              }));
          }
        } catch (error) {
          console.error('Error fetching reviews:', error);
          // Continue with empty reviews instead of failing
        }

        // Transform API data to match component structure
        const transformedProduct = {
          id: productData.id,
          name: productData.name,
          price: parseFloat(productData.price),
          salePrice: parseFloat(productData.sale_price),
          finalPrice: parseFloat(productData.final_price),
          activeOffers: productData.active_offers,
          variants: productData.variants,
          images: productData.variants.reduce((acc, variant) => {
            if (!variant.images) return acc;
            const variantImages = variant.images.map(img => ({
              url: img.images,
              alt: `${productData.name} - ${variant.color}`
            }));
            return [...acc, ...variantImages];
          }, []),
          description: productData.description,
          category: productData.category?.name,
          subcategory: productData.brand?.name,
          colors: productData.variants.map(variant => ({
            name: variant.color,
            code: variant.color_code
          })),
          sizes: [...new Set(productData.variants.map(variant => variant.size))],
          specifications: productData.specifications?.reduce((acc, spec) => ({
            ...acc,
            [spec.specification]: spec.specification
          }), {}),
          highlights: productData.highlights?.map(h => h.text) || [],
          styling: productData.styling_tips?.map(tip => tip.styling_tips) || [],
          deliveryInfo: {
            makeToOrder: true,
            productionTime: productData.production_time,
            shipping: productData.shipping,
            returns: productData.returns_policy
          },
          tags: productData.tags?.map(tag => tag.name) || [],
          sku: productData.variants[0]?.sku,
          stock: productData.variants.reduce((total, variant) => total + (variant.stock || 0), 0)
        };

        setProduct(transformedProduct);
        setReviews(productReviews);
        
        // Fetch related products from same category
        if (productData.category?.id) {
          const relatedData = await userApi.getRelatedProducts(productData.category.id);
          const relatedProducts = relatedData
            .filter(p => p.id !== productData.id)
            .slice(0, 3)
            .map(p => ({
              id: p.id,
              name: p.name,
              price: p.price,
              image: p.variants[0]?.images[0]?.images || '',
              category: p.category?.name
            }));
          setRelatedProducts(relatedProducts);
        }

      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error(error.message || 'Failed to load product details');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id, navigate]);

  const handleAddToCart = async () => {
    // Create loading toast and store its ID
    const toastId = toast.loading('Adding to cart...');
    
    try {
      // Debug log to see product structure
      console.log('Product data:', product);

      // Validation checks
      if (!selectedSize) {
        toast.dismiss(toastId);
        toast.error('Please select a size');
        return;
      }

      if (!selectedColor) {
        toast.dismiss(toastId);
        toast.error('Please select a color');
        return;
      }

      if (quantity <= 0) {
        toast.dismiss(toastId);
        toast.error('Please select a valid quantity');
        return;
      }

      // Get variant ID from the product data
      const variant = product.variants.find(v => 
        v.size === selectedSize && 
        v.color.toLowerCase() === selectedColor.toLowerCase()
      );

      if (!variant) {
        console.error('Selected combination:', { size: selectedSize, color: selectedColor });
        console.error('Available variants:', product.variants);
        toast.dismiss(toastId);
        toast.error('Selected combination is not available');
        return;
      }

      // Check stock at variant level
      if (quantity > variant.stock) {
        toast.dismiss(toastId);
        toast.error(`Only ${variant.stock} items available in this size and color`);
        return;
      }

      // Call API to add to cart
      await userApi.addItemToCart({
        product_id: product.id,
        variant_id: variant.id,
        quantity: quantity
      });

      // Dispatch custom events
      window.dispatchEvent(new Event('cartUpdated'));
      window.dispatchEvent(new Event('openCart'));

      // Show success toast
      toast.dismiss(toastId);
      toast.success(
        <div className="flex flex-col">
          <span>Added to cart successfully</span>
          <span className="text-sm text-gray-500">
            {quantity} x {product.name} ({selectedSize}, {selectedColor})
          </span>
        </div>,
        {
          duration: 3000,
          icon: '🛍️'
        }
      );

    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.dismiss(toastId);
      
      if (error.message?.includes('login')) {
        toast.error('Please login to add items to cart');
        navigate('/login');
        return;
      }

      if (error.message?.includes('stock')) {
        toast.error('Item out of stock');
        return;
      }

      toast.error(error.message || 'Failed to add to cart');
    }
  };

  useEffect(() => {
    const checkWishlistStatus = async () => {
      if (!id) return;
      
      try {
        setIsWishlistLoading(true);
        const wishlistData = await userApi.getWishlist();
        const isInList = wishlistData.some(item => 
          item.product?.id === parseInt(id)
        );
        setIsInWishlist(isInList);
      } catch (error) {
        console.error('Error checking wishlist status:', error);
      } finally {
        setIsWishlistLoading(false);
      }
    };

    checkWishlistStatus();

    // Listen for wishlist updates from other components
    const handleWishlistUpdate = () => {
      checkWishlistStatus();
    };

    window.addEventListener('wishlistUpdated', handleWishlistUpdate);
    return () => {
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
    };
  }, [id]);

  const toggleWishlist = async () => {
    if (!id) return;

    try {
      setIsWishlistLoading(true);
      
      if (isInWishlist) {
        await userApi.removeFromWishlist(parseInt(id));
        setIsInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await userApi.addToWishlist(parseInt(id));
        setIsInWishlist(true);
        toast.success('Added to wishlist');
      }
      
      // Dispatch event to update other components
      window.dispatchEvent(new Event('wishlistUpdated'));
    } catch (error) {
      if (error.message.includes('login')) {
        navigate('/login');
        return;
      }
      
      // Handle the case where item already exists
      if (error.message.includes('already exists')) {
        setIsInWishlist(true);
      } else {
        toast.error(error.message || 'Failed to update wishlist');
      }
    } finally {
      setIsWishlistLoading(false);
    }
  };

  useEffect(() => {
    // Fetch related products
    const fetchRelatedProducts = () => {
      const allProducts = JSON.parse(localStorage.getItem('products') || '[]');
      const related = allProducts
        .filter(p => p.category === product?.category && p.id !== product?.id)
        .slice(0, 3);
      setRelatedProducts(related);
    };

    if (product) {
      fetchRelatedProducts();
    }
  }, [product]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2C3E50] border-t-transparent"></div>
      </div>
    );
  }

  if (!product) return null;

  // Handle full screen navigation
  const handlePrevImage = () => {
    setSelectedImage(prev => 
      prev === 0 ? product.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImage(prev => 
      prev === product.images.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <>
      <Helmet>
        <title>{product.name} | Nilormy</title>
        <meta name="description" content={product.description} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li><a href="/" className="hover:text-gray-900">Home</a></li>
            <li>/</li>
            <li><a href="/products" className="hover:text-gray-900">Products</a></li>
            <li>/</li>
            <li className="text-gray-900">{product.name}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Left Column - Product Images & Reviews */}
          <div className="space-y-8">
            {/* Image Gallery Section */}
            <div className="space-y-4">
              {/* Main Image */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="aspect-w-3 aspect-h-4 overflow-hidden bg-gray-50 relative"
                onMouseEnter={() => !isMobile && setShowMagnifier(true)}
                onMouseLeave={() => !isMobile && setShowMagnifier(false)}
                onMouseMove={!isMobile ? handleMouseMove : undefined}
                onClick={() => setShowImageViewer(true)}
              >
                <img
                  ref={imageRef}
                  src={product?.images?.[selectedImage]?.url}
                  alt={product?.images?.[selectedImage]?.alt}
                  className="w-full h-full object-cover"
                  style={{ pointerEvents: 'none' }}  // Add this to prevent image dragging
                />
                {/* Out of Stock Overlay */}
                {!product?.stock && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center">
                    <div className="bg-red-500 text-white px-6 py-3 rounded-lg transform -rotate-12">
                      <span className="text-xl font-bold">Out of Stock</span>
                    </div>
                  </div>
                )}
                {!isMobile && showMagnifier && (
                  <div 
                    className="absolute w-[200px] h-[200px] pointer-events-none border border-gray-200"
                    style={{
                      top: `calc(${magnifierPosition.y}% - 100px)`,
                      left: `calc(${magnifierPosition.x}% - 100px)`,
                      backgroundImage: `url(${product?.images?.[selectedImage]?.url})`,
                      backgroundPosition: `${magnifierPosition.x}% ${magnifierPosition.y}%`,
                      backgroundSize: '500%',  // Increased zoom level
                      backgroundRepeat: 'no-repeat',
                      zIndex: 2,
                      borderRadius: '50%',
                      boxShadow: '0 0 10px rgba(0,0,0,0.2)',
                    }}
                  />
                )}
              </motion.div>

              {/* Image Viewer */}
              <AnimatePresence>
                {showImageViewer && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black z-[9999] touch-none"
                    style={{ 
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: '100vh',
                      width: '100%',
                      margin: 0,
                      padding: 0
                    }}
                    onClick={() => setShowImageViewer(false)}
                  >
                    {/* Close button */}
                    <button
                      className="absolute top-4 right-4 text-white z-10 p-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowImageViewer(false);
                      }}
                    >
                      <FiX className="w-6 h-6" />
                    </button>

                    {/* Navigation buttons */}
                    <button
                      className="absolute top-1/2 -translate-y-1/2 left-4 text-white z-10 p-2 hover:bg-white/10 rounded-full transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                    >
                      <FiChevronLeft className="w-8 h-8" />
                    </button>

                    <button
                      className="absolute top-1/2 -translate-y-1/2 right-4 text-white z-10 p-2 hover:bg-white/10 rounded-full transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                    >
                      <FiChevronRight className="w-8 h-8" />
                    </button>

                    {/* Full screen image */}
                    <motion.div
                      className="w-full h-full flex items-center justify-center bg-black"
                      drag="x"
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.2}
                      onDragEnd={(e, { offset, velocity }) => {
                        const swipe = Math.abs(offset.x) * velocity.x;
                        if (swipe < -100) handleNextImage();
                        if (swipe > 100) handlePrevImage();
                      }}
                    >
                      <AnimatePresence mode="wait">
                        <motion.img
                          key={selectedImage}
                          initial={{ opacity: 0, x: 100 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -100 }}
                          transition={{ duration: 0.3 }}
                          src={product?.images?.[selectedImage]?.url}
                          alt={product?.images?.[selectedImage]?.alt}
                          className="w-full h-full object-contain select-none"
                          style={{
                            maxHeight: '100vh',
                            width: '100%',
                            margin: 0,
                            padding: 0
                          }}
                          draggable="false"
                        />
                      </AnimatePresence>
                    </motion.div>

                    {/* Image counter */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                      {selectedImage + 1} / {product?.images?.length}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Thumbnail Gallery */}
              {product?.images && (
                <div className="relative">
                  <div 
                    className="flex gap-2 overflow-x-hidden"
                    style={{ scrollBehavior: 'smooth' }}
                  >
                    {product.images.slice(0, 4).map((image, index) => (
                      <motion.button
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => setSelectedImage(index)}
                        className={`
                          relative flex-none w-1/4 aspect-square overflow-hidden
                          ${selectedImage === index 
                            ? 'ring-2 ring-[#2C3E50]' 
                            : 'hover:opacity-80'
                          }
                          transition-opacity duration-200
                        `}
                      >
                        <img
                          src={image.url}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                        />
                        {!product?.stock && (
                          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />
                        )}
                        {selectedImage === index && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="absolute inset-0 bg-white bg-opacity-10"
                          />
                        )}
                      </motion.button>
                    ))}
                  </div>

                  {/* Navigation arrows - only show if more than 4 images */}
                  {product.images.length > 4 && (
                    <>
                      <button
                        onClick={() => setSelectedImage(prev => 
                          prev === 0 ? product.images.length - 1 : prev - 1
                        )}
                        className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 p-1 rounded-full shadow-md hover:bg-white transition-colors"
                      >
                        <FiChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSelectedImage(prev => 
                          prev === product.images.length - 1 ? 0 : prev + 1
                        )}
                        className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 p-1 rounded-full shadow-md hover:bg-white transition-colors"
                      >
                        <FiChevronRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Optional: Image Navigation Dots for Mobile */}
              <div className="flex justify-center space-x-2 sm:hidden mt-4">
                {product?.images?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-2 h-2 transition-all duration-200 ${
                      selectedImage === index 
                        ? 'bg-gray-800 w-4' 
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              {/* Optional: Image Count Indicator */}
              <div className="hidden sm:block absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 text-sm">
                {selectedImage + 1} / {product?.images?.length}
              </div>
            </div>

            {/* Reviews Section */}
            <div className="border-t pt-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-medium">Customer Reviews</h3>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, index) => (
                      <FiStar
                        key={index}
                        className={`w-5 h-5 ${
                          index < (reviews.length > 0 
                            ? Math.round(reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length)
                            : 0)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">({reviews.length} reviews)</span>
                </div>
              </div>

              <div className="space-y-6">
                {reviews.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No reviews yet</p>
                ) : (
                  reviews.map((review) => (
                    <div key={review.id} className="border-b pb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{review.customerName}</span>
                        <span className="text-sm text-gray-500">{review.date}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {[...Array(5)].map((_, index) => (
                            <FiStar
                              key={index}
                              className={`w-4 h-4 ${
                                index < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm">{review.review}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Enhanced Product Details */}
          <div className="space-y-8">
            {/* Product Title and Price */}
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-[#2C3E50] font-medium">{product?.category}</p>
                <h1 className="text-3xl font-light text-gray-900">{product?.name}</h1>
              </div>
              <div className="mt-4 space-y-6">
                <div className="flex items-end gap-4">
                  {product?.activeOffers?.length > 0 ? (
                    <>
                      {/* Offer Price */}
                      <div className="flex flex-col">
                        <span className="text-3xl font-semibold text-red-600">
                          ₹{product.finalPrice?.toLocaleString('en-IN')}
                        </span>
                        <span className="text-lg text-gray-500 line-through">
                          ₹{product.price?.toLocaleString('en-IN')}
                        </span>
                      </div>
                      
                      {/* Offer Badge */}
                      <div className="bg-red-100 px-3 py-1 rounded-full">
                        <span className="text-red-600 font-medium">
                          {product.activeOffers[0].offer_type === 'PERCENTAGE' 
                            ? `${product.activeOffers[0].discount_value}%`
                            : `₹${product.activeOffers[0].discount_value}`} OFF
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Regular/Sale Price */}
                      <div className="flex flex-col">
                        <span className="text-3xl font-semibold text-gray-900">
                          ₹{(product.salePrice || product.price)?.toLocaleString('en-IN')}
                        </span>
                        {product.salePrice && (
                          <span className="text-lg text-gray-500 line-through">
                            ₹{product.price?.toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Show active offer name if exists */}
                {product?.activeOffers?.length > 0 && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-red-700 text-sm font-medium">
                      Offer: {product.activeOffers[0].name}
                    </p>
                    <p className="text-red-600 text-xs">
                      Valid till {new Date(product.activeOffers[0].end_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-[#2C3E50] bg-blue-50 px-3 py-1 rounded-full text-sm">
                  {product?.tags?.[0]}
                </span>
              </div>
            </div>

            {/* Product Actions Section */}
            <div className="space-y-6">
              {/* Available Colors */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">Select Color</span>
                  <span className="text-xs text-gray-500">
                    {[...new Set(product?.colors?.map(color => color.name))].length} colors available
                  </span>
                </div>
                
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {Array.from(new Set(product?.colors?.map(color => color.name)))
                    .map((colorName, index) => {
                      // Get the first color object that matches this name
                      const color = product?.colors?.find(c => c.name === colorName);
                      return (
                        <motion.button
                          key={colorName}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          onClick={() => setSelectedColor(colorName)}
                          className={`
                            group relative p-3 rounded-xl transition-all duration-200
                            ${selectedColor === colorName 
                              ? 'ring-2 ring-[#2C3E50] bg-gray-50'
                              : 'hover:bg-gray-50 border border-gray-100'
                            }
                          `}
                        >
                          <div className="flex items-center gap-2">
                            {/* Color Preview */}
                            <div className="relative">
                              <div 
                                className={`
                                  w-8 h-8 rounded-full shadow-sm transition-transform duration-200
                                  group-hover:scale-110 group-hover:shadow-md
                                  ${selectedColor === colorName ? 'scale-110' : ''}
                                `}
                                style={{ 
                                  backgroundColor: color.code,
                                  border: color.code.toLowerCase() === '#ffffff' ? '1px solid #e5e7eb' : 'none'
                                }}
                              />
                              {selectedColor === colorName && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="absolute -top-1 -right-1 w-4 h-4 bg-[#2C3E50] rounded-full flex items-center justify-center"
                                >
                                  <FiCheck className="w-2.5 h-2.5 text-white" />
                                </motion.div>
                              )}
                            </div>

                            {/* Color Name Only */}
                            <div className="flex-1 text-left">
                              <p className="font-medium text-gray-900 text-sm">{colorName}</p>
                            </div>
                          </div>

                          {/* Hover Effect */}
                          <motion.div
                            initial={false}
                            animate={{ opacity: selectedColor === colorName ? 1 : 0 }}
                            className="absolute inset-0 rounded-xl bg-gradient-to-b from-gray-50/50 to-transparent pointer-events-none"
                          />
                        </motion.button>
                      );
                    })}
                </div>

                {/* Selected Color Preview */}
                {selectedColor && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
                  >
                    <div 
                      className="w-6 h-6 rounded-full shadow-sm"
                      style={{ 
                        backgroundColor: product?.colors?.find(c => c.name === selectedColor)?.code,
                        border: product?.colors?.find(c => c.name === selectedColor)?.code.toLowerCase() === '#ffffff' 
                          ? '1px solid #e5e7eb' 
                          : 'none'
                      }}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Selected: {selectedColor}</p>
                      <p className="text-xs text-gray-500">
                        {product?.colors?.find(c => c.name === selectedColor)?.code}
                      </p>
                    </div>
                    <button 
                      onClick={() => setSelectedColor(null)}
                      className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <FiX className="w-4 h-4 text-gray-500" />
                    </button>
                  </motion.div>
                )}
              </div>

              {/* Size Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">Select Size</span>
                  <button 
                    onClick={() => setShowSizeGuide(true)}
                    className="text-sm text-[#2C3E50] hover:underline flex items-center space-x-1"
                  >
                    <FiInfo className="w-4 h-4" />
                    <span onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering parent onClick
                      window.open('/size-guide', '_blank');
                    }}>Size Guide</span>
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {product?.variants
                    // Get unique sizes from variants that have stock
                    ?.filter(variant => variant.stock > 0)
                    ?.reduce((sizes, variant) => {
                      if (!sizes.includes(variant.size)) {
                        sizes.push(variant.size);
                      }
                      return sizes;
                    }, [])
                    .sort((a, b) => {
                      // Custom sorting order for sizes
                      const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Free Size'];
                      return sizeOrder.indexOf(a) - sizeOrder.indexOf(b);
                    })
                    .map((size) => {
                      // Check if this size is available in the selected color
                      const isAvailableInColor = !selectedColor || 
                        product.variants.some(v => 
                          v.size === size && 
                          v.color.toLowerCase() === selectedColor.toLowerCase() && 
                          v.stock > 0
                        );

                      return (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          disabled={!isAvailableInColor}
                          className={`
                            py-3 px-4 text-sm font-medium transition-all relative
                            ${selectedSize === size 
                              ? 'bg-[#2C3E50] text-white outline outline-1 outline-[#2C3E50]' 
                              : isAvailableInColor
                                ? 'bg-white text-gray-900 border border-gray-200 hover:border-[#2C3E50]'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                            }
                          `}
                        >
                          {size}
                          {!isAvailableInColor && (
                            <span className="absolute -top-1 -right-1">
                              <FiX className="w-3 h-3 text-gray-400" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                </div>
                {selectedColor && !product?.variants.some(v => 
                  v.color.toLowerCase() === selectedColor.toLowerCase() && v.stock > 0
                ) && (
                  <p className="text-sm text-red-500 mt-2">
                    Selected color is out of stock in all sizes
                  </p>
                )}
              </div>

              {/* Quantity Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">Select Quantity</span>
                    <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded">
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        !product?.stock ? 'bg-red-500' :
                        product?.stock <= 5 ? 'bg-red-500' : 
                        product?.stock <= 10 ? 'bg-amber-500' : 
                        'bg-emerald-500'
                      }`} />
                      <span className="text-xs font-medium text-gray-600">
                        {!product?.stock ? 'Out of Stock' :
                         product?.stock <= 5 ? 'Low Stock' :
                         product?.stock <= 10 ? 'Limited Stock' :
                         'In Stock'
                        }
                      </span>
                    </div>
                  </div>
                  {product?.stock > 0 && (
                    <span className="text-xs text-gray-500">
                      {product.stock} pieces available
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                    className={`
                      flex items-center justify-center
                      w-10 h-10 rounded-full
                      shadow-md
                      transition-all duration-300
                      ${quantity <= 1 
                        ? 'bg-gray-100 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-[#2C3E50] to-[#4A6B8A] hover:shadow-lg hover:translate-y-[-2px] active:translate-y-[0px]'
                      }
                    `}
                    disabled={quantity <= 1}
                  >
                    <FiMinus 
                      className={`
                        w-4 h-4
                        ${quantity <= 1 ? 'text-gray-400' : 'text-white'}
                      `}
                    />
                  </button>
                  
                  <div className="w-14 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-lg shadow-sm">
                    <span className="text-base font-medium">{quantity}</span>
                  </div>
                  
                  <button
                    onClick={() => quantity < product?.stock && setQuantity(quantity + 1)}
                    className={`
                      w-10 h-10 flex items-center justify-center rounded-full
                      shadow-md
                      transition-all duration-300
                      ${!product?.stock || quantity >= product?.stock
                        ? 'bg-gray-100 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-[#2C3E50] to-[#4A6B8A] hover:shadow-lg hover:translate-y-[-2px] active:translate-y-[0px]'
                      }
                    `}
                    disabled={!product?.stock || quantity >= product?.stock}
                  >
                    <FiPlus className={`w-4 h-4 ${
                      !product?.stock || quantity >= product?.stock ? 'text-gray-400' : 'text-white'
                    }`} />
                  </button>
                </div>

                {/* Stock warning messages */}
                {product?.stock > 0 && product?.stock <= 5 && (
                  <div className="flex items-center gap-2 bg-red-50 px-3 py-1.5 rounded-md">
                    <FiAlertCircle className="w-4 h-4 text-red-500" />
                    <p className="text-xs font-medium text-red-600 animate-pulse">
                      Only {product.stock} left in stock
                    </p>
                  </div>
                )}

                {!product?.stock && (
                  <div className="flex items-center gap-2 bg-red-50 px-3 py-1.5 rounded-md">
                    <FiAlertCircle className="w-4 h-4 text-red-500" />
                    <p className="text-xs font-medium text-red-600">
                      Currently out of stock
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-12 gap-2">
                {/* Add to Collection Button */}
                <button
                  onClick={handleAddToCart}
                  className="col-span-8 py-4 bg-[#2C3E50] text-white hover:bg-[#34495E] transition-colors flex items-center justify-center space-x-2"
                >
                  <FiShoppingCart className="w-5 h-5" />
                  <span>Add to Cart</span>
                </button>

                {/* Save Button */}
                <button
                  onClick={toggleWishlist}
                  disabled={isWishlistLoading}
                  className={`
                    col-span-2 py-4 border flex items-center justify-center
                    transition-all duration-300
                    ${isWishlistLoading ? 'opacity-50 cursor-not-allowed' : ''}
                    ${isInWishlist 
                      ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100' 
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }
                  `}
                  aria-label={isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
                >
                  {isWishlistLoading ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FiHeart 
                      className={`w-5 h-5 transition-all duration-300 ${
                        isInWishlist ? 'fill-current text-red-600' : ''
                      }`} 
                    />
                  )}
                </button>

                {/* Share Button */}
                <button
                  onClick={handleShare}
                  className="col-span-2 py-4 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  <FiShare2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Product Details Accordion */}
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">Product Highlights</h3>
                <ul className="grid grid-cols-2 gap-3">
                  {product?.highlights?.map((highlight, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                      <span className="w-2 h-2 bg-[#2C3E50] rounded-full"></span>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">Product Description</h3>
                <p className="text-gray-600 leading-relaxed">{product?.description}</p>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">Specifications</h3>
                <p className="text-gray-600 leading-relaxed">
                  {Object.entries(product?.specifications || {})
                    .map(([_, value]) => {
                      const displayValue = Array.isArray(value) ? value.join(', ') : value;
                      return displayValue;
                    })
                    .join(' • ')
                  }
                </p>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-medium mb-3">Styling Tips</h3>
                <ul className="space-y-2">
                  {product?.styling?.map((tip, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                      <span className="w-2 h-2 bg-[#2C3E50] rounded-full mt-1.5"></span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Delivery Information */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="font-medium flex items-center space-x-2">
                <FiInfo className="w-5 h-5 text-[#2C3E50]" />
                <span>Delivery Information</span>
              </h3>
              <div className="text-sm text-gray-600 space-y-2">
                <p>• Made to order: {product?.deliveryInfo?.productionTime}</p>
                <p>• {product?.deliveryInfo?.shipping}</p>
                <p>• {product?.deliveryInfo?.returns}</p>
              </div>
            </div>
          </div>
        </div>

        {/* You May Also Love Section */}
        <section className="mt-24 mb-16">
          <div className="relative">
            {/* Decorative Elements */}
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-6 text-2xl font-light text-gray-900">
                You May Also Love
              </span>
            </div>
          </div>

          {/* Products Grid */}
          <div className="mt-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
              {relatedProducts.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative"
                >
                  {/* Image Container */}
                  <div className="relative aspect-[3/4] overflow-hidden bg-gray-50">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                    
                    {/* Overlay with Actions */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 md:group-hover:bg-opacity-20 transition-all duration-300">
                      <div className={`
                        absolute bottom-4 left-4 right-4 flex gap-2
                        md:transform md:translate-y-full md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100
                        transition-all duration-300
                      `}>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex-1 bg-white backdrop-blur-sm bg-opacity-90 py-2.5 px-4 rounded-lg text-sm font-medium text-gray-900 hover:bg-opacity-100 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/product/${item.id}`);
                          }}
                        >
                          <FiEye className="w-4 h-4" />
                          <span className="hidden sm:inline">View Product</span>
                          <span className="sm:hidden">View</span>
                        </motion.button>
                        

                      </div>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="mt-4 space-y-2">
                    <h3 className="text-sm font-medium text-gray-900 line-clamp-1">
                      {item.name}
                    </h3>
                    
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">{item.category}</p>
                      <div className="flex items-center gap-2">
                        {item.salePrice ? (
                          <>
                            <span className="text-sm font-medium text-red-600">
                              ₹{item.salePrice.toLocaleString()}
                            </span>
                            <span className="text-sm text-gray-500 line-through">
                              ₹{item.price.toLocaleString()}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm font-medium text-gray-900">
                            ₹{item.price.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* View All Button */}
          <div className="mt-12 text-center">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 text-[#2C3E50] font-medium hover:text-gray-900 transition-colors"
              onClick={() => navigate('/products')}
            >
              View All Products
              <FiArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductView;













































